import React, { useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  list: {
    padding: theme.spacing(2, 2, 2, 1),
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  item: {
    display: "flex",
    alignItems: "flex-start",
    position: "relative",
    paddingBottom: theme.spacing(2),
    cursor: "pointer",
  },
  bullet: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#1976d2",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    fontWeight: 600,
    flexShrink: 0,
    zIndex: 1,
  },
  connector: {
    position: "absolute",
    left: 13,
    top: 28,
    bottom: 0,
    width: 2,
    backgroundColor: "#1976d2",
  },
  label: {
    marginLeft: theme.spacing(1.5),
    paddingTop: 4,
    fontSize: "0.85rem",
    color: "#212121",
    lineHeight: 1.3,
    overflowWrap:'anywhere'
  },
  selectedLabel: {
    color: "#1976d2",
    textDecoration: "underline",
    fontWeight: 500,
  },
  version: {
    marginLeft: theme.spacing(1.5),
    fontSize: "0.78rem",
    fontStyle: "italic",
    color: "#555",
    lineHeight: 1.3,
  },
}));

export default function ForecastHistoryList({
  history,
  loading,
  selectedHistoryId,
  onSelect,
  filters,
}) {
  const classes = useStyles();

  // Build incremental decimal versions per month+year group (oldest = lowest, newest = highest).
  // Major version starts at 1 and grows by 1–3 per item (derived from syncID).
  // Minor version is a single digit derived from syncID, giving a natural random look.
  const versionMap = useMemo(() => {
    const groups = {};
    (history || []).forEach((h) => {
      const key = moment(h.forecastSyncDate).format("YYYY-MM");
      if (!groups[key]) groups[key] = [];
      groups[key].push(h);
    });
    const map = {};
    Object.values(groups).forEach((group) => {
      // Sort ascending so oldest gets the lowest version
      const ascending = [...group].sort(
        (a, b) => new Date(a.forecastSyncDate) - new Date(b.forecastSyncDate)
      );
      let major = 1;
      ascending.forEach((h) => {
        const id = h.collabrixForecastSyncID;
        const minor = id % 10;
        map[id] = `v${String(major).padStart(2, "0")}.${minor}`;
        // Increment major by 1, 2, or 3 for the next item
        major += 1 + (id % 3);
      });
    });
    return map;
  }, [history]);

  if (loading) {
    return (
      <Box className={classes.empty}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Box className={classes.empty}>
        <Typography variant="body2">No history available.</Typography>
      </Box>
    );
  }

  const isCMO = localStorage.getItem("collabType") === "CMO";
  const orgShort = isCMO
    ? (filters.sponsorLabel || "").replace(/\s+/g, "_")
    : (filters.cmoLabel || "").replace(/\s+/g, "_");
  const brandShort = (filters.brand || "").replace(/\s+/g, "_");

  return (
    <Box className={classes.list}>
      {history.map((h, idx) => {
        const isSelected = selectedHistoryId === h.collabrixForecastSyncID;
        const isLast = idx === history.length - 1;
        const ts = moment(h.forecastSyncDate)
          .format("DD-MMM-YYYY HH:mm:ss")
          .toUpperCase();
        const label = `${orgShort || (isCMO ? "Sponsor" : "CMO")}_${
          brandShort || "Brand"
        }_Forecast_\n${ts}`;
        return (
          <Box
            key={h.collabrixForecastSyncID}
            className={classes.item}
            onClick={() => onSelect(h.collabrixForecastSyncID)}
          >
            {!isLast && <span className={classes.connector} />}
            <span className={classes.bullet}>{idx + 1}</span>
            <Box>
              <Typography
                className={`${classes.label} ${
                  isSelected ? classes.selectedLabel : ""
                }`}
                style={{ whiteSpace: "pre-line" }}
              >
                {label}
              </Typography>
              <Typography className={classes.version}>
                {versionMap[h.collabrixForecastSyncID] || "v01.0"}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
